import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import {
  Row,
  Col,
  Card,
  Input,
  Upload,
  Tooltip,
  Popconfirm,
  Modal,
  Form,
  Typography,
  message,
  Spin,
  List,
  Tag,
  Alert,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { getUploadedFileDimensions } from "../../../../core/utils/dataManipulating/images";
import sizeValidation from "../../../shared/upload/sizeValidation";
import dimensionValidation from "../../../shared/upload/dimensionValidation";
import { multiImageZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";
import { getImageType } from "../../../../core/utils/general/strings";
import BulkUploadResultsModal from "./creatives/BulkUploadResultModal";
import { invalidImageDimensionErrorMessage } from "../../../../core/utils/constants/constants";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import "./../../Creatives/library/creativestyle.css";
import Button from "../../../shared/globalStyling/Button";
import SearchBar from "../../Creatives/library/SearchBar";
import AddCreativesModal from "../../Creatives/components/AddCreativesModal";

const allowedImageTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/avif",
];
const { Dragger } = Upload;

const CreativeTab = ({
  onChangeStateValue,
  creativeName,
  onDeleteMedias,
  onCreateAdgroup,
  ids,
  setIds,
  setRenewedCreatives,
  creativeKeys,
  bulkCreateOrgMedia,
  searchText,
  handleSearch,
  clearAllSelections,
}) => {
  const { refetchMedias, createOrgMedia, orgMedias, adGroupCreatives, updateAdGroupCreatives } =
    useLibraryCreativesContext() || {};

  /* eslint-disable */
  useEffect(() => {
    const getNewOrgMedias =
      orgMedias &&
      orgMedias
        .filter(val => val.type && val.type.toUpperCase() !== "VIDEO") // Filter out videos
        .map(val => {
          const urlOrgMedia = Storage.get(val.key, {
            expires: 600000,
          });

          urlOrgMedia.then(response => {
            return (val.url = response);
          });
          return val;
        });

    setRenewedCreatives(getNewOrgMedias);
  }, [orgMedias]);
  /* eslint-enable */


  // Unified upload state like VideoTab
  const [uploadState, setUploadState] = useState({
    mode: "idle", // 'idle', 'single', 'bulk'
    files: [],
    validationResults: [],
    /* UNUSED: uploadProgress - not implemented for progress tracking
    uploadProgress: {},
    errors: [], // Not used in current implementation
    */
    currentFile: null,
    imageUrl: null,
    imageDimensions: null,
    key: null,
  });
  const [
    showUploadResultsTableModal,
    setShowUploadResultsTableModal,
  ] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);

  const [adGroupModal, setAdGroupModal] = useState(false);
  const [adGroupModalVisible, setAdGroupModalVisible] = useState(false);
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState([]);
  const [selectedAdUnits, setSelectedAdUnits] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const [form] = Form.useForm();
  const [formAdGroup] = Form.useForm();
  const [uploadResult, setUploadResults] = useState([]);

  const { Title } = Typography;

  // Clear fields after upload or cancel - like VideoTab
  const clearFields = async () => {
    form.resetFields();
    setUploadState({
      mode: "idle",
      files: [],
      validationResults: [],
      currentFile: null,
      imageUrl: null,
      imageDimensions: null,
      key: null,
    });
    onChangeStateValue("creativeName", "");
  };

  // Unified file validation function - like VideoTab
  const validateImageFile = async file => {
    const errors = [];

    // Check file extension as well as MIME type for more robust validation
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['png', 'jpeg', 'jpg', 'gif', 'avif'];
    
    // Check file type (MIME type)
    if (!allowedImageTypes.includes(file.type)) {
      errors.push(
        `Invalid file type "${file.type}". Only PNG, JPEG, JPG, GIF, AVIF are allowed.`
      );
    }

    // Additional check for file extension
    if (!allowedExtensions.includes(fileExtension)) {
      errors.push(
        `Invalid file extension ".${fileExtension}". Only .png, .jpeg, .jpg, .gif, .avif are allowed.`
      );
    }

    // Check file size
    if (!sizeValidation(file.size)) {
      errors.push("Image must be smaller than 2MB!");
    }

    // If basic validation fails, don't proceed with dimension check
    if (errors.length > 0) {
      return {
        valid: false,
        errors,
        dimensions: { width: 0, height: 0 },
        file,
      };
    }

    try {
      // Get dimensions
      const dimensions = await getUploadedFileDimensions(file);
      // Check dimensions
      if (!dimensionValidation(dimensions.width, dimensions.height, true)) {
        errors.push(invalidImageDimensionErrorMessage);
      }

      return {
        valid: errors.length === 0,
        errors,
        dimensions,
        file,
      };
    } catch (error) {
      console.error('Error analyzing image file:', error);
      errors.push("Failed to analyze image file. File may be corrupted or not a valid image.");
      return {
        valid: false,
        errors,
        dimensions: { width: 0, height: 0 },
        file,
      };
    }
  };

  // Smart file selection handler - like VideoTab
  const handleUnifiedFileSelection = async info => {
    const files = info.fileList.map(
      fileInfo => fileInfo.originFileObj || fileInfo
    );

    if (files.length === 0) {
      setUploadState(prev => ({ ...prev, mode: "idle", files: [] }));
      return;
    }

    // Validate all files
    const validationResults = await Promise.all(
      files.map(file => validateImageFile(file))
    );

    const mode = files.length === 1 ? "single" : "bulk";

    setUploadState({
      mode,
      files,
      validationResults,
      currentFile: files.length === 1 ? files[0] : null,
      imageUrl: null,
      imageDimensions: null,
      key: null,
    });

    /* UNUSED: invalidFiles variable - computed but never used for user feedback
    // Check for validation errors (no popup message)
    const invalidFiles = validationResults.filter(result => !result.valid);
    */

    // For single file mode, set up preview if valid
    if (mode === "single" && validationResults[0].valid) {
      await setupSingleFilePreview(files[0], validationResults[0]);
    }
  };

  // Setup single file preview - like VideoTab
  const setupSingleFilePreview = async (file, validationResult) => {
    try {
      const fname = `${Date.now()}-${file.name}`;
      const response = await Storage.put(fname, file, {
        contentType: file.type,
      });
      const url = await Storage.get(response.key, { expires: 600000 });

      setUploadState(prev => ({
        ...prev,
        imageUrl: url,
        imageDimensions: validationResult.dimensions,
        key: response.key,
      }));

      // Auto-fill form name
      if (!form.getFieldValue("name")) {
        const fileName = file.name.replace(/\.[^/.]+$/, "");
        form.setFieldsValue({ name: fileName });
        onChangeStateValue("creativeName", fileName);
      }
    } catch (error) {
      message.error("Failed to upload file for preview");
    }
  };

  // Unified upload modal content - like VideoTab
  const renderUnifiedUploadContent = () => {
    const { mode, files, validationResults } = uploadState;

    if (mode === "single") {
      return (
        <div>
          <Title level={5}>
            Upload Display Creative (or select multiple files)
          </Title>
          <Dragger
            multiple={true}
            accept=".png, .jpeg, .jpg, .gif, .avif"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleUnifiedFileSelection}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Drop image file(s) here or click to browse
            </p>
            <p className="ant-upload-hint">
              Supports PNG, JPEG, JPG, GIF, AVIF files
              <br />
              💡 Hold Ctrl/Cmd while clicking to select multiple files at once
            </p>
          </Dragger>

          {uploadState.imageUrl && (
            <div style={{ marginTop: 16 }}>
              <img
                src={uploadState.imageUrl}
                alt="preview"
                style={{
                  width: "100%",
                  maxHeight: "300px",
                  objectFit: "contain",
                }}
              />
              <p style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                You can select more files above to switch to bulk upload mode
              </p>
            </div>
          )}

          {validationResults.length > 0 && !validationResults[0].valid && (
            <Alert
              message="File Upload Error"
              description={
                <div>
                  {validationResults[0].errors.map((error, index) => (
                    <div key={index} style={{ marginBottom: 4 }}>
                      • {error}
                    </div>
                  ))}
                </div>
              }
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      );
    }

    if (mode === "bulk") {
      return (
        <div>
          <Title level={5}>
            Bulk Upload Display Creatives ({files.length} files)
          </Title>
          <Dragger
            multiple={true}
            accept=".png, .jpeg, .jpg, .gif, .avif"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleUnifiedFileSelection}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Drop image files here or click to browse
            </p>
            <p className="ant-upload-hint">
              Supports multiple PNG, JPEG, JPG, GIF, AVIF files
            </p>
          </Dragger>

          <List
            size="small"
            dataSource={files}
            style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              marginBottom: 16
            }}
            renderItem={(file, index) => {
              const validation = validationResults[index];
              return (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeFileFromBulk(index)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={file.name}
                    description={
                      <div>
                        <div>
                          Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        {validation && (
                          <div>
                            {validation.valid ? (
                              <Tag color="green">Valid</Tag>
                            ) : (
                              <div>
                                {validation.errors.map((error, errorIndex) => (
                                  <Tag
                                    key={errorIndex}
                                    color="red"
                                    style={{ marginBottom: 2 }}
                                  >
                                    {error}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
          
          {/* Show overall validation summary for bulk upload */}
          {validationResults.length > 0 && (
            (() => {
              const invalidCount = validationResults.filter(result => !result.valid).length;
              const validCount = validationResults.filter(result => result.valid).length;
              
              if (invalidCount > 0) {
                return (
                  <Alert
                    message={`${invalidCount} of ${validationResults.length} files have validation errors`}
                    description={`${validCount} files are valid and ready to upload. Please fix the errors in the invalid files or remove them before proceeding.`}
                    type="warning"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                );
              }
              
              return (
                <Alert
                  message={`All ${validCount} files are valid`}
                  description="All files passed validation and are ready to upload."
                  type="success"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              );
            })()
          )}
        </div>
      );
    }

    // Default idle state
    return (
      <div>
        <Title level={5}>Upload Display Creative(s)</Title>
        <Dragger
          multiple={true}
          accept=".png, .jpeg, .jpg, .gif, .avif"
          showUploadList={false}
          beforeUpload={() => false}
          onChange={handleUnifiedFileSelection}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Drop image file(s) here or click to browse
          </p>
          <p className="ant-upload-hint">
            Supports single or multiple PNG, JPEG, JPG, GIF, AVIF files
            <br />
            💡 Select multiple files at once using Ctrl/Cmd + Click
          </p>
        </Dragger>
      </div>
    );
  };

  // Remove file from bulk upload - like VideoTab
  const removeFileFromBulk = index => {
    const newFiles = uploadState.files.filter((_, i) => i !== index);
    const newValidationResults = uploadState.validationResults.filter(
      (_, i) => i !== index
    );

    const newMode =
      newFiles.length === 0
        ? "idle"
        : newFiles.length === 1
        ? "single"
        : "bulk";

    setUploadState(prev => ({
      ...prev,
      mode: newMode,
      files: newFiles,
      validationResults: newValidationResults,
    }));

    // If switching to single mode, setup preview
    if (
      newMode === "single" &&
      newValidationResults[0] &&
      newValidationResults[0].valid
    ) {
      setupSingleFilePreview(newFiles[0], newValidationResults[0]);
    }
  };

  // Modal title based on upload mode - like VideoTab
  const getModalTitle = () => {
    const { mode, files } = uploadState;
    switch (mode) {
      case "single":
        return "Upload Display Creative";
      case "bulk":
        return `Bulk Upload Display Creatives (${files.length} files)`;
      default:
        return "Upload Display Creative(s)";
    }
  };

  // Modal footer based on upload mode - like VideoTab
  const getModalFooter = () => {
    const { mode, validationResults } = uploadState;

    if (mode === "single") {
      const isValid =
        validationResults.length > 0 && validationResults[0].valid;
      return [
        <Button
          key="cancel"
          onClick={() => {
            clearFields();
            setUploadModal(false);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!isValid || uploadLoading}
          onClick={() => form.submit()}
        >
          Save Creative
        </Button>,
      ];
    }

    if (mode === "bulk") {
      const validFiles = validationResults.filter(result => result.valid);
      const hasValidFiles = validFiles.length > 0;

      return [
        <Button
          key="cancel"
          onClick={() => {
            clearFields();
            setUploadModal(false);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!hasValidFiles || uploadLoading}
          onClick={handleBulkUpload}
        >
          Upload {validFiles.length} Creative
          {validFiles.length !== 1 ? "s" : ""}
        </Button>,
      ];
    }

    return [
      <Button
        key="cancel"
        onClick={() => {
          clearFields();
          setUploadModal(false);
        }}
      >
        Cancel
      </Button>,
    ];
  };

  const handleSubmit = async () => {
    setUploadLoading(true);

    try {
      // Validate using unified upload state
      const {
        validationResults,
        key: uploadKey,
        imageUrl: uploadImageUrl,
        imageDimensions: uploadDimensions,
      } = uploadState;

      if (!uploadKey || !uploadImageUrl) {
        message.error("Please select a file to upload");
        setUploadLoading(false);
        return;
      }

      if (validationResults.length === 0 || !validationResults[0].valid) {
        message.error("Please upload a valid image file");
        setUploadLoading(false);
        return;
      }

      const { file } = validationResults[0];
      const fileName = form.getFieldValue("name") || creativeName;

      const CapitalizeCreativeName =
        fileName.charAt(0).toUpperCase() + fileName.slice(1);

      await createOrgMedia({
        variables: {
          mediatype: "STILL",
          key: uploadKey,
          url: uploadImageUrl,
          width: uploadDimensions.width,
          height: uploadDimensions.height,
          name: CapitalizeCreativeName,
          size: file.size,
          fileType: getImageType(file.type),
        },
      });

      message.success("Display Creative saved successfully!");

      await refetchMedias();

      clearFields();
      setUploadModal(false);
      setUploadLoading(false);
    } catch (error) {
      setUploadLoading(false);
      message.error(`File upload failed: ${error.message}`);
    }
  };

  // Handle bulk upload - like VideoTab
  const handleBulkUpload = async () => {
    const { validationResults } = uploadState;
    const validFiles = validationResults.filter(result => result.valid);

    if (validFiles.length === 0) {
      message.error("No valid files to upload");
      return;
    }

    setUploadLoading(true);
    setUploadComplete(false);
    setUploadResults([]);
    setUploadModal(false);
    setShowUploadResultsTableModal(true);

    const results = [];

    try {
      for (const validFile of validFiles) {
        const { file, dimensions } = validFile;

        // Upload to S3
        const fname = `${Date.now()}-${file.name}`;
        const response = await Storage.put(fname, file, {
          contentType: "image/*",
        });
        const url = await Storage.get(response.key, { expires: 600000 });

        // Prepare result object for UI
        const result = {
          name: file.name.replace(/\.[^/.]+$/, ""),
          dimensions: `${dimensions.width}x${dimensions.height}`,
          fileSize: file.size,
          status: true,
        };

        // Update progress
        setUploadResults(prev => [...prev, result]);

        // Prepare for bulk create
        results.push({
          type: "STILL",
          key: response.key,
          url: url,
          width: dimensions.width,
          height: dimensions.height,
          name: file.name.replace(/\.[^/.]+$/, ""),
          size: file.size,
          fileType: getImageType(file.type),
        });
      }

      // Bulk create all images
      await bulkCreateOrgMedia(results, [], true);
      await refetchMedias();

      setUploadComplete(true);
      message.success(`Successfully uploaded ${results.length} creative(s)`);

      // Auto-close modal after 2 seconds like VideoTab
      setTimeout(() => {
        setShowUploadResultsTableModal(false);
      }, 2000);
    } catch (error) {
      message.error("Bulk upload failed");
    } finally {
      setUploadLoading(false);
      clearFields();
    }
  };

  const handleCreateAdGroup = async () => {
    try {
      const values = await formAdGroup.validateFields();
      const adGroupName = values.name;

      const remakeIds = ids.map(idVal => {
        const getId = idVal;
        return { id: getId };
      });

      // Pass the data directly to avoid state timing issues
      await onCreateAdgroup(adGroupName, remakeIds);

      // Clean up modal state after successful creation
      // Note: selection clearing is handled by parent LibraryCreativesViewImproved
      formAdGroup.resetFields();
      setAdGroupModal(false);
      setAdGroupModalVisible(false);
    } catch (error) {
      message.error('Failed to create ad group');
    }
  };

  const handleDeleteMedias = async () => {
    try {
      await onDeleteMedias(ids);
      await setRenewedCreatives(orgMedias.filter((item) => !ids.includes(item.id)));
      await setIds([]);
    } catch (err) {
      message.error(`Error deleting media creative(s): ${err}`);
    }
  };

  // Handle assigning display creatives to ad groups
  const handleAssignAdUnitsInternal = async (selectedAdGroupIds, selectedIds) => {
    setAssigning(true);
    try {
      // Use the proper assignment approach - connect media to existing ad groups
      await Promise.all(
        selectedAdGroupIds.map(adGroupId =>
          updateAdGroupCreatives({
            variables: {
              id: adGroupId,
              data: {
                medias: {
                  connect: selectedIds.map(id => ({ id })),
                },
              },
            },
          })
        )
      );
      message.success("Display creatives assigned to ad group(s)!");
      setAdGroupModalVisible(false);
      setSelectedAdGroupIds([]);
      setSelectedAdUnits([]);

      // Clear local selections immediately
      setIds([]);

      // Clear all selections through parent state management
      if (clearAllSelections) {
        clearAllSelections();
      }

      await refetchMedias();
    } catch (error) {
      message.error("Failed to assign display creatives to ad group(s)");
    } finally {
      setAssigning(false);
    }
  };
  return (
    <>
      <BulkUploadResultsModal
        visible={showUploadResultsTableModal}
        uploadResults={uploadResult}
        uploadInProgress={uploadLoading}
        uploadComplete={uploadComplete}
        onCancel={() => {
          setShowUploadResultsTableModal(false);
        }}
      />
      <Modal
        title={getModalTitle()}
        open={uploadModal}
        closable={!uploadLoading}
        maskClosable={!uploadLoading}
        keyboard={!uploadLoading}
        centered
        width={uploadState.mode === "bulk" ? 800 : 600}
        style={{ maxHeight: '90vh' }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        footer={getModalFooter()}
        onCancel={() => {
          clearFields();
          setUploadModal(false);
        }}
      >
        <Spin spinning={uploadLoading}>
          {uploadState.mode === "single" ? (
            <Form form={form} onFinish={handleSubmit}>
              <Row justify="center" align="middle">
                <Col span={24}>
                  {renderUnifiedUploadContent()}

                  {uploadState.imageUrl && (
                    <Form.Item
                      label="Creative Name"
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter creative name",
                        },
                      ]}
                      style={{ marginTop: 16 }}
                    >
                      <Input placeholder="Enter creative name" />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Form>
          ) : (
            <div>{renderUnifiedUploadContent()}</div>
          )}
        </Spin>
      </Modal>
      <AddCreativesModal
        setAdGroupModalVisible={setAdGroupModalVisible}
        adGroupModalVisible={adGroupModalVisible}
        setAdGroupModal={setAdGroupModal}
        ids={ids} // selected ad units (just ids)
        orgMedias={orgMedias} // for table info
        selectedAdUnits={selectedAdUnits} // selected adunits (full info)
        adGroupCreatives={adGroupCreatives} // available adgroups
        selectedAdGroupIds={selectedAdGroupIds}
        setSelectedAdGroupIds={setSelectedAdGroupIds}
        handleAssignAdUnits={handleAssignAdUnitsInternal}
        assigning={assigning}
      />
      <Modal
        title={"NEW DISPLAY AD GROUP"}
        open={adGroupModal}
        centered
        footer={null}
        closable={false}
      >
        <Form layout="vertical" onFinish={handleCreateAdGroup} form={formAdGroup}>
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <Form.Item
                label="Ad Group Name"
                name="name"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="Name of Ad Group"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={24}>
              <div style={{ display: "flex", justifyContent: "end" }}>
                <Button
                  htmlType="button"
                  type="default"
                  onClick={() => {
                    setAdGroupModal(false);
                    formAdGroup.resetFields();
                  }}
                >
                  Back
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                >
                  Create
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Row>
        <Col xs={24} xl={24}>
          <Card bodyStyle={{ padding: 10 }}>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col>
                <Title level={4}>Display Ad Units</Title>
              </Col>
              <Col>
                <SearchBar searchText={searchText} onSearch={handleSearch} />
              </Col>
            </Row>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col xs={24} sm={16} md={16} lg={14}>
                <Row>
                  <Popconfirm
                    placement="topLeft"
                    title={`Are you sure you want to delete the selected creatives? This action cannot be undone.`}
                    onConfirm={() => handleDeleteMedias()}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Tooltip placement="bottom" title={`Delete selected creatives`}>
                      <Button
                        type="default"
                        disabled={!ids || ids.length === 0}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Button
                    type="default"
                    disabled={!ids || ids.length === 0}
                    onClick={() =>
                      multiImageZipKey(
                        'Creatives (' + creativeKeys.length + ' images)',
                        creativeKeys
                      )
                    }
                  >
                    Download Ad Unit
                  </Button>
                  <Button
                    type="default"
                    disabled={!ids || ids.length === 0}
                    onClick={() => {
                      setSelectedAdUnits(
                        orgMedias.filter(item => ids.includes(item.id))
                      );
                      setAdGroupModalVisible(true);
                    }}
                  >
                    Assign to Ad Groups
                  </Button>
                </Row>
              </Col>
              <Col xs={24} sm={8} md={8} lg={10} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModal(true)}
                  disabled={false}
                >
                  Upload Display Creative(s)
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CreativeTab;
